import { gql } from '@apollo/client/core'
import type { Ref } from 'vue'

import type { RaidId } from '../../types'

/**
 * RSVP writes are acknowledgements only. The selected raid's
 * `boardChanged` subscription delivers the authoritative `hasSignedIn` flag
 * and any Pool placement change to every open planner tab.
 */
const RaidRsvpSetDocument = gql`
  mutation RaidRsvpSet($guildId: Int!, $raidId: Int!, $signedIn: Boolean!) {
    raidRsvpSet(guildId: $guildId, raidId: $raidId, signedIn: $signedIn)
  }
`

export interface RaidRsvpSetMutation {
  readonly raidRsvpSet: boolean
}

type ReportingRunner = <T>(
  mutation: unknown,
  variables: Record<string, unknown>,
  acknowledge?: (result: T) => boolean,
) => Promise<boolean>

export interface RaidRsvpActionsCtx {
  readonly activeDiscordGuildId: Readonly<Ref<string>>
  readonly activeRaidId: Ref<RaidId | null>
  readonly error: Ref<string | null>
  readonly runReporting: ReportingRunner
}

export interface RaidRsvpActionsApi {
  /** Set the signed-in session player's RSVP for the selected raid. */
  readonly setRaidRsvp: (signedIn: boolean) => Promise<boolean>
}

export function useRaidRsvpActions(ctx: RaidRsvpActionsCtx): RaidRsvpActionsApi {
  async function setRaidRsvp(signedIn: boolean): Promise<boolean> {
    const guildId = ctx.activeDiscordGuildId.value
    const raidId = ctx.activeRaidId.value
    if (guildId === '' || guildId === '0') {
      ctx.error.value = 'Select a guild before changing your raid sign-up.'
      return false
    }
    if (raidId === null) {
      ctx.error.value = 'Select a raid before changing your sign-up.'
      return false
    }
    const acknowledged = await ctx.runReporting<RaidRsvpSetMutation>(
      RaidRsvpSetDocument,
      {
        guildId,
        raidId: Number(raidId),
        signedIn,
      },
      (result) => result.raidRsvpSet,
    )
    return acknowledged && ctx.activeRaidId.value === raidId
  }

  return { setRaidRsvp }
}
